import {
  AudioLinesIcon,
  BlocksIcon,
  BookMarkedIcon,
  BrainCircuitIcon,
  ChartPieIcon,
  ClipboardListIcon,
  CreditCardIcon,
  FolderCodeIcon,
  GoalIcon,
  HeadphonesIcon,
  HomeIcon,
  KeyIcon,
  MessagesSquareIcon,
  MonitorIcon,
  SettingsIcon,
  ShieldCheckIcon,
  UserIcon,
  UsersIcon,
  UsersRoundIcon,
  WalletIcon,
  WebhookIcon,
  ZapIcon,
} from 'lucide-react';

import { DEVELOPER_MODULES, DEVELOPERS_SETTINGS_PATHS } from '@/modules/developerTools/constants';
import { actions } from '@/shared/types/permissions';

import { type PageEntry } from './types';
import { matchesPath } from './utils';

export const PAGE_REGISTRY: readonly PageEntry[] = [
  // Main
  { id: 'home', i18nKey: 'main_navigation.home', path: '/', icon: HomeIcon, group: 'main' },
  {
    id: 'ai-agents',
    i18nKey: 'common.ai_agents',
    path: '/ai-agents',
    icon: ZapIcon,
    group: 'main',
  },
  {
    id: 'knowledge-bases',
    i18nKey: 'common.collections',
    path: '/knowledge-bases',
    icon: BookMarkedIcon,
    group: 'main',
  },
  {
    id: 'campaigns',
    i18nKey: 'common.campaigns',
    path: '/campaigns',
    icon: GoalIcon,
    group: 'main',
  },
  {
    id: 'contacts',
    i18nKey: 'contacts.title',
    path: '/contacts',
    icon: UsersRoundIcon,
    group: 'main',
  },
  {
    id: 'debts',
    i18nKey: 'contacts.debts.title',
    path: '/debts',
    icon: WalletIcon,
    group: 'main',
    requiredPermission: { module: 'contacts', action: actions.READ },
  },
  {
    id: 'chat',
    i18nKey: 'main_navigation.chat',
    path: '/chats',
    icon: MessagesSquareIcon,
    group: 'main',
  },

  // Analytics
  {
    id: 'analytics-overview',
    i18nKey: 'analytics.overview',
    path: '/analytics/overview',
    icon: ChartPieIcon,
    group: 'analytics',
    parentI18nKey: 'main_navigation.analytics',
  },
  {
    id: 'analytics-chat-logs',
    i18nKey: 'analytics.account_chat_logs',
    path: '/analytics/account-chat-logs',
    icon: ChartPieIcon,
    group: 'analytics',
    parentI18nKey: 'main_navigation.analytics',
  },

  // Deploy
  {
    id: 'devtools',
    i18nKey: 'developer_tools.title',
    path: '/devtools',
    icon: FolderCodeIcon,
    group: 'deploy',
  },
  {
    id: 'connections',
    i18nKey: 'main_navigation.connections',
    path: '/integrations',
    icon: BlocksIcon,
    group: 'main',
  },
  { id: 'rules', i18nKey: 'main_navigation.rules', path: '/rules', icon: ZapIcon, group: 'deploy' },

  // Settings
  {
    id: 'account-settings',
    i18nKey: 'profile.account_settings',
    path: '/account/information',
    icon: SettingsIcon,
    group: 'settings',
  },
  {
    id: 'activity-logs',
    i18nKey: 'pages.activity_logs',
    path: '/account/activity-logs',
    icon: ClipboardListIcon,
    group: 'settings',
  },

  // Settings — sub-pages
  {
    id: 'billing',
    i18nKey: 'pages.billing_simple',
    path: '/account/billing',
    icon: CreditCardIcon,
    group: 'settings',
    parentI18nKey: 'profile.account_settings',
    requiredPermission: { module: 'billing', action: actions.READ },
  },
  {
    id: 'human-agents',
    i18nKey: 'pages.human_agents',
    path: '/account/human-agents',
    icon: HeadphonesIcon,
    group: 'settings',
    parentI18nKey: 'profile.account_settings',
  },
  {
    id: 'data-privacy',
    i18nKey: 'pages.data_privacy_simple',
    path: '/account/data-privacy',
    icon: ShieldCheckIcon,
    group: 'settings',
    parentI18nKey: 'profile.account_settings',
  },
  {
    id: 'member-access',
    i18nKey: 'common.members',
    path: '/account/manage/member-access',
    icon: UsersIcon,
    group: 'settings',
    parentI18nKey: 'profile.account_settings',
  },
  {
    id: 'team-access',
    i18nKey: 'common.teams',
    path: '/account/manage/team-access',
    icon: UsersIcon,
    group: 'settings',
    parentI18nKey: 'profile.account_settings',
  },

  // Settings — developers
  {
    id: 'developers',
    i18nKey: 'developer_tools.developers',
    path: '/account/developers',
    icon: FolderCodeIcon,
    group: 'settings',
    requiredPermission: { module: DEVELOPER_MODULES, action: actions.READ },
    // A flag-gated child page must not keep the parent visible on its own
    hideWithoutChildren: true,
  },
  {
    id: 'developers-api-keys',
    i18nKey: 'developer_tools.api_keys',
    path: DEVELOPERS_SETTINGS_PATHS.apiKeys,
    icon: KeyIcon,
    group: 'settings',
    parentI18nKey: 'developer_tools.developers',
    requiredPermission: { module: 'credentials', action: actions.READ },
  },
  {
    id: 'developers-llms',
    i18nKey: 'developer_tools.llms',
    path: DEVELOPERS_SETTINGS_PATHS.llms,
    icon: BrainCircuitIcon,
    group: 'settings',
    parentI18nKey: 'developer_tools.developers',
    requiredPermission: { module: 'language_models', action: actions.READ },
  },
  {
    id: 'developers-speech-models',
    i18nKey: 'developer_tools.speech_models.title',
    path: DEVELOPERS_SETTINGS_PATHS.speechModels,
    icon: AudioLinesIcon,
    group: 'settings',
    parentI18nKey: 'developer_tools.developers',
    requiredPermission: { module: 'speech_models', action: actions.READ },
    featureFlag: 'speech_strategy',
  },
  {
    id: 'developers-event-notifications',
    i18nKey: 'developer_tools.plugins',
    path: DEVELOPERS_SETTINGS_PATHS.eventNotifications,
    icon: WebhookIcon,
    group: 'settings',
    parentI18nKey: 'developer_tools.developers',
    requiredPermission: { module: 'plugins', action: actions.READ },
  },

  // Main — environments
  {
    id: 'environments',
    i18nKey: 'main_navigation.environments',
    path: '/environments',
    icon: MonitorIcon,
    group: 'main',
  },

  // Profile (fullPath — outside /:slug)
  {
    id: 'personal-info',
    i18nKey: 'pages.profile',
    path: '/profile/information',
    icon: UserIcon,
    group: 'profile',
    fullPath: true,
  },
];

/** Finds the PAGE_REGISTRY entry whose path is the longest prefix of `relativePath`. */
export const findBestPageMatch = (
  relativePath: string,
  filter?: (entry: PageEntry) => boolean
): PageEntry | null => {
  let best: PageEntry | null = null;
  let bestLen = 0;

  for (const entry of PAGE_REGISTRY) {
    if (filter && !filter(entry)) {
      continue;
    }
    const isMatch = matchesPath(entry.path, relativePath);
    if (isMatch && entry.path.length > bestLen) {
      bestLen = entry.path.length;
      best = entry;
    }
  }

  return best;
};
